'use strict';

import { FqComponents } from "../index";
import { DateTime } from "@fiqas/utils";
import { Directive, Inject, NgUtils } from "@fiqas/angular-utils";
import * as angular from 'angular';

export interface ITimeFormatterParameter {
    basedate: string;
    minvalue: string;
    maxvalue: string;
    onblur: string;
}

@Directive({
    module: FqComponents,
    selector: 'timeFormatter'
})
export class TimeFormatter implements angular.IDirective {
    static numbers = [48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 96, 97, 98, 99, 100, 101, 102, 103, 104, 105];
    static arrows = [37, 38, 39, 40];
    static semicolon = 186;
    static escape = 27;
    static backspace = 8;
    static del = 46;
    static enter = 13;
    static tab = 9;
    static others = [TimeFormatter.backspace, TimeFormatter.tab, TimeFormatter.enter, 16, 17, 18, TimeFormatter.escape, 45, TimeFormatter.del];

    constructor(
        @Inject('$filter') private $filter: angular.IFilterService,
        @Inject('$timeout') private $timeout: angular.ITimeoutService,
        @Inject('$parse') private $parse: angular.IParseService) {
    }

    priority = 1000;
    restrict = 'A';
    require = 'ngModel';

    rawBaseDate: string;
    _baseDate: Date;
    get baseDate() {
        return this._baseDate ? this._baseDate.clone() : null;
    }
    set baseDate(value: Date) {
        this._baseDate = value;
    }

    rawMinValue: string;
    _minDate: Date;
    get minDate() {
        return !!this._minDate ? this._minDate.clone() : null;
    }
    set minDate(value: Date) {
        this._minDate = value;
    }

    rawMaxValue: string;
    _maxDate: Date;
    get maxDate() {
        return !!this._maxDate ? this._maxDate.clone() : null;
    }
    set maxDate(value: Date) {
        this._maxDate = value;
    }

    link($scope: angular.IScope, $elem: angular.IAugmentedJQuery, $attr: angular.IAttributes, ctrl: angular.INgModelController) {
        var params = <ITimeFormatterParameter>angular.fromJson($attr["timeFormatter"]);

        this.rawBaseDate = NgUtils.ParseExpressionOrValue(params.basedate, this.$parse, $scope);
        this.baseDate = Date.parseOrNull(this.rawBaseDate);

        this.rawMinValue = NgUtils.ParseExpressionOrValue(params.minvalue || "", this.$parse, $scope);
        this.minDate = !this.rawMinValue ? null :
            this.rawMinValue.length == 5 && this.rawMinValue.indexOf(":") > -1 ? DateTime.TimeToDateTime(this.rawMinValue, this.baseDate) :
                <Date>new Date(this.rawMinValue);

        this.rawMaxValue = NgUtils.ParseExpressionOrValue(params.maxvalue || "", this.$parse, $scope);
        this.maxDate = !this.rawMaxValue ? null :
            this.rawMaxValue.length == 5 && this.rawMaxValue.indexOf(":") > -1 ? DateTime.TimeToDateTime(this.rawMaxValue, this.baseDate) :
                Date.parseOrNull(this.rawMaxValue);

        $elem.bind("keydown", (e) => {
            if (TimeFormatter.others.concat(TimeFormatter.arrows).concat(TimeFormatter.numbers).indexOf(e.keyCode) == -1) {
                e.preventDefault();
                if (e.keyCode != TimeFormatter.semicolon)
                    return;
            }
            if (e.keyCode == 37 || e.keyCode == 39)
                e.stopPropagation();

            var value = $elem.val();
            if (e.keyCode == TimeFormatter.backspace || e.keyCode == TimeFormatter.del) {
                if (value.length == 3)
                    $elem.val(value.replace(":", ""));
            } else if (e.keyCode == TimeFormatter.semicolon && value.length == 1) {
                $elem.val("0" + value + ":");
            } else if ([TimeFormatter.semicolon].concat(TimeFormatter.numbers).indexOf(e.keyCode) > -1) {
                if (value.length == 2)
                    $elem.val(value + ":");
            } else if (e.keyCode == TimeFormatter.escape) {
                $elem.val(undefined);
                ctrl.$rollbackViewValue();
                $elem.blur();
            } else if (e.keyCode == TimeFormatter.enter) {
                $elem.blur();
            } else if (e.keyCode == TimeFormatter.tab) {
                if (value.length > 0 && value.length < 5) {
                    if (value.length == 1)
                        value = "0" + value;
                    value = (value + "000").split("");
                    value[2] = ":";
                    value = value.slice(0, 5).join("");
                    $elem.val(value);
                    ctrl.$setViewValue(value, "tab");
                }
            }

        });

        $elem.bind('blur', (e) => {
            var onblur = this.$parse(params.onblur);
            if (onblur) {
                onblur($scope, { $value: $elem.val() });
            }
        });

        $elem.bind("keyup", (e) => {
            var values = $elem.val().split(":");
            if (values.length > 1 && values[1] > 59)
                values[1] = 59;
            if (values[0] > 23) {
                values[0] = 24;
                if (values.length > 1) {
                    var nv = "";
                    for (var v = 0; v < values[1].length; v++)
                        nv += "0";
                    values[1] = nv;
                }
            }
            $elem.val(values.join(":"));
        });

        ctrl.$parsers.push(v=> this.parse(ctrl, v));
        ctrl.$formatters.push(v=> this.format(v));
    }

    parse(ctrl: angular.INgModelController, val: any) {
        if (val.toISOString)
            return val.toISOString();

        if (val.length == 5) {
            var date = new Date(ctrl.$modelValue);

            if (this.baseDate.isValid())
                date = this.baseDate;
            else if (!date.isValid())
                date = new Date();

            var minutes = val.substr(3, 2);
            if (minutes > 59)
                minutes = 59;

            var hours = val.substr(0, 2);
            if (hours > 23) {
                hours = 24;
                minutes = 0;
            }
            date.setHours(hours);
            date.setMinutes(minutes);

            if (date.isValid()) {
                if (this.minDate && date < this.minDate)
                    return this.minDate.toISOString();
                if (this.maxDate && date > this.maxDate)
                    return this.maxDate.toISOString();

                return date.toISOString();
            }
        }
        return ctrl.$modelValue;
    }

    format(val) {
        if (val) {
            var date = Date.parseOrNull(val)
            if (date != null)
                return this.$filter('date')(date, "HH:mm");
        }
        return "";
    }
}